// ABOUTME: Hook that resolves brand config from the API using the URL path slug or localStorage.
// ABOUTME: Cache is used for instant initial render, but the API response always overwrites it.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { enrichBrand } from "./brandConfig";
import { useGetBrandQuery } from "../../../newapi/global/brandRootSlice";

const SLUG_KEY = "brand_slug";
const BRAND_KEY = "brand_data";
const BRAND_EXPIRY_KEY = "brand_expiry";
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

const getSavedBrand = () => {
    try {
        const expiry = localStorage.getItem(BRAND_EXPIRY_KEY);
        if (!expiry || Date.now() > parseInt(expiry, 10)) {
            localStorage.removeItem(BRAND_KEY);
            localStorage.removeItem(BRAND_EXPIRY_KEY);
            localStorage.removeItem(SLUG_KEY);
            return { slug: null, brand: null };
        }
        const slug = localStorage.getItem(SLUG_KEY);
        const brand = localStorage.getItem(BRAND_KEY);
        return {
            slug,
            brand: brand ? JSON.parse(brand) : null,
        };
    } catch (e) {
        return { slug: null, brand: null };
    }
};

const saveBrand = (slug, brand) => {
    localStorage.setItem(SLUG_KEY, slug);
    localStorage.setItem(BRAND_KEY, JSON.stringify(brand));
    localStorage.setItem(BRAND_EXPIRY_KEY, String(Date.now() + EXPIRY_MS));
};

const useBranding = () => {
    const { brandSlug } = useParams();
    const saved = getSavedBrand();

    // Use slug from URL, or fall back to saved slug from localStorage
    const activeSlug = brandSlug || saved.slug;

    // Use cache for instant initial render
    const hasCached = activeSlug && saved.slug === activeSlug && saved.brand !== null;

    const [brand, setBrand] = useState(hasCached ? saved.brand : null);

    // Always fetch from API when we have a slug — cache only avoids the loading state
    const { data: apiData, isLoading: apiLoading } = useGetBrandQuery(activeSlug, {
        skip: !activeSlug,
    });

    useEffect(() => {
        if (!apiData || !activeSlug) return;

        const raw = apiData[activeSlug];
        if (!raw) return;

        const enriched = enrichBrand(raw);
        setBrand(enriched);
        saveBrand(activeSlug, enriched);
    }, [apiData, activeSlug]);

    useEffect(() => {
        if (brandSlug) {
            localStorage.setItem(SLUG_KEY, brandSlug);
        }
    }, [brandSlug]);

    // Only show loading if we have no cache to display
    const isLoading = !hasCached && !!activeSlug && apiLoading;

    return { brand, isLoading, fromCache: hasCached, brandSlug: activeSlug };
};

export default useBranding;
